import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuthLayout from '../auth/AuthLayout';

export default function CreateFamily() {
  const navigate = useNavigate();
  const { user, family, createFamily, loading: authLoading, error: authError } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [familyName, setFamilyName] = useState('');
  const [memberNames, setMemberNames] = useState(['']);

  // Redirect if not logged in or already has family
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/signup');
      } else if (family) {
        navigate('/success');
      } else {
        // Pre-fill user's name as first member
        const userName = user.user_metadata?.full_name || user.user_metadata?.name || '';
        if (userName && memberNames[0] === '') {
          setMemberNames([userName]);
        }
      }
    }
  }, [user, family, authLoading, navigate]);

  const addMember = () => {
    if (memberNames.length < 10) {
      setMemberNames([...memberNames, '']);
    }
  };

  const removeMember = (index) => {
    if (memberNames.length > 1) {
      setMemberNames(memberNames.filter((_, i) => i !== index));
    }
  };

  const updateMember = (index, value) => {
    const updated = [...memberNames];
    updated[index] = value;
    setMemberNames(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!familyName.trim()) {
      setError('Please enter a family name');
      return;
    }

    const validMembers = memberNames.filter(n => n.trim());
    if (validMembers.length === 0) {
      setError('Please add at least one family member');
      return;
    }

    setLoading(true);

    try {
      await createFamily({
        familyName,
        memberNames: validMembers
      });
      navigate('/success');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <AuthLayout>
        <div className="flex justify-center py-8">
          <svg className="animate-spin h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Create your family" subtitle="Add your family members who will contribute memories">
      <form onSubmit={handleSubmit} className="space-y-6">
        {(error || authError) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error || authError}
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
          Welcome, {user?.user_metadata?.full_name || user?.email}! Let's set up your family.
        </div>

        <div>
          <label htmlFor="familyName" className="label">Family name</label>
          <input
            id="familyName"
            type="text"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            className="input"
            placeholder="The Smiths"
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            This will be the name of your family newsletter
          </p>
        </div>

        <div>
          <label className="label">Family members</label>
          <p className="text-xs text-gray-500 mb-3">
            Add everyone who will submit photos and stories
          </p>

          <div className="space-y-3">
            {memberNames.map((name, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => updateMember(index, e.target.value)}
                  className="input flex-1"
                  placeholder={index === 0 ? 'Your name' : `Family member ${index + 1}`}
                />
                {memberNames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMember(index)}
                    className="px-3 py-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove member"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {memberNames.length < 10 && (
            <button
              type="button"
              onClick={addMember}
              className="mt-3 text-sm text-primary-600 hover:text-primary-500 font-medium flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add another member
            </button>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full py-3"
        >
          {loading ? 'Creating...' : 'Create Family'}
        </button>
      </form>
    </AuthLayout>
  );
}
